// Support System — Internal Rollout & Communication Plan.
//
// Content-only module so the plan can be revised without touching the page.
// Anything in square brackets is a placeholder the process owner must fill in
// before this is circulated — names and dates are deliberately not invented here.

export const PLAN_DOC = {
  id: 'NI-SUP-ROLL-001',
  title: 'Support System — Internal Rollout & Communication Plan',
  revision: 'Rev 01',
  status: 'Draft — for review',
  date: '2026-08-12',
  owner: 'Support process owner — to be assigned',
  relatedFlow: 'NI-SUP-FLOW-001 — Support loop, end-to-end flow',
};

export const PLAN_REVISIONS = [
  {
    rev: 'Rev 01',
    date: '2026-08-12',
    status: 'Draft — for review',
    summary: 'First consolidated plan, built out from the six-point internal draft.',
    changes: [
      'Restructured to Objective → Audience → Key messages → Roles → Channels → Timeline → Training → Feedback, as intended.',
      'Added an explicit scope boundary — what belongs in the system and what does not.',
      'Replaced "no Teams, no email" with a workable rule: discuss anywhere, decide and record in the case.',
      'Added measurable success criteria, response-time expectations and an out-of-hours path.',
      'Added a risk register for the rollout itself, and leadership behaviours that drive adoption.',
      'Added ready-to-send announcement, manager talking points and a one-page quick card.',
    ],
  },
];

export const HEADLINE = {
  eyebrow: 'The message in one paragraph',
  text: 'We are putting one defined Support process and one system behind it. From go-live, support cases are registered and followed in that system — so we have one source of truth, one named owner per case, and a history we can learn from. The purpose is not administration. It is that we stop solving the same problem twice, and that the work Support does becomes visible enough to fix the causes.',
};

export const SECTIONS = [
  /* ------------------------------------------------------------ 1. objective */
  {
    id: 'objective',
    n: '1',
    title: 'Objective',
    lead: 'What this rollout is for, and how we will know it worked.',
    blocks: [
      {
        type: 'p',
        text: 'Make the Support system the defined way of working: everyone knows what it is, what they are expected to do, and where support communication belongs. Success is not "the system is live" — it is that a case can be understood by someone who was not there, and that the causes behind recurring cases actually get fixed.',
      },
      {
        type: 'cards',
        items: [
          { title: 'One source of truth', text: 'The case is the record. Not an inbox, not a chat thread, not somebody\'s memory.' },
          { title: 'Clear ownership', text: 'Every case has one named owner at every moment. Ownership transfers explicitly; it is never simply "with the team".' },
          { title: 'Less information loss', text: 'What was tried, what was found and what was decided survives holidays, handovers and staff changes.' },
          { title: 'Easier escalation', text: 'Escalating is a defined step with a defined summary, not a favour asked over a desk.' },
          { title: 'Usable history', text: 'Recurring issues become countable. A unit\'s support history is available the next time it is touched.' },
          { title: 'Reporting that means something', text: 'Support load, causes and cost per case become facts we can act on — feeding the < 2/1000 warranty target.' },
        ],
      },
      {
        type: 'callout',
        tone: 'green',
        title: 'The link to the number that matters',
        text: 'The support flow exists to serve one target: fewer than 2 of every 1000 devices produced should end in accepted warranty. That target is unreachable while support work is invisible — you cannot rank causes you never recorded. This rollout is how the data starts existing.',
      },
    ],
  },

  /* --------------------------------------------------------- 2. what changes */
  {
    id: 'change',
    n: '2',
    title: 'What is changing — and what stops',
    lead: 'Change lands when people know what to stop doing, not only what to start.',
    blocks: [
      {
        type: 'rule',
        do: [
          'Every support request gets registered in the system — including the ones that arrive as a tap on the shoulder.',
          'One named owner per case, from creation to close.',
          'Findings, decisions and customer replies are written on the case.',
          'Internal experts are pulled in through the case, so their answer is captured.',
          'Escalation happens through the defined step, with a summary of what was already tried.',
          'Closing means the cause and the fix are written down in words the next reader can use.',
        ],
        dont: [
          'Do not run a support case privately in your inbox.',
          'Do not let a Teams thread be the only place a decision exists.',
          'Do not answer a customer directly on something you have not registered.',
          'Do not forward a case to an individual instead of escalating it in the system.',
          'Do not close a case with "resolved", "fixed" or "OK now".',
          'Do not bounce a customer to the form because they used the wrong channel — register it for them.',
        ],
      },
      {
        type: 'callout',
        tone: 'amber',
        title: 'The rule that decides whether this works',
        text: 'Discuss anywhere. Decide and record in the case. Banning Teams and email is not realistic and trying to will cost you credibility on day one — people ask each other for help because it is fast, and that is a strength. The rule that works is narrower and enforceable: if a conversation changed the case, the outcome goes on the case, by whoever had the conversation, the same day. Teams is where you think. The case is where it counts.',
      },
      {
        type: 'p',
        text: 'That single rule replaces the "no scattered emails or chats" instruction in the original draft. It gets the same outcome — nothing important living only in a private thread — without asking people to work in a way nobody works.',
      },
    ],
  },

  /* ------------------------------------------------------------ 3. why & scope */
  {
    id: 'scope',
    n: '3',
    title: 'Scope — what belongs in the system',
    lead: 'Without a boundary people either log everything or dismiss the rule entirely. Both are worse than a clear line.',
    blocks: [
      {
        type: 'table',
        columns: ['Goes in the support system', 'Does not'],
        rows: [
          ['A customer or partner reports a fault, defect or unexpected behaviour', 'Commercial negotiation, pricing and quoting'],
          ['A customer or partner asks a technical or how-to question', 'Order status and delivery chasing (that is order handling)'],
          ['A complaint about the product or about our service', 'Internal project work with no customer question behind it'],
          ['A warranty enquiry or claim', 'HR, IT and facilities requests'],
          ['A commissioning or installation problem on site', 'Prospect and pre-sales enquiries with no delivered unit — unless they turn technical'],
          ['A request routed to us via a partner or distributor', 'Internal ideas and improvement suggestions with no case behind them'],
          ['A repeat of a previously closed issue on the same unit', 'General product feedback with no problem to resolve'],
        ],
      },
      {
        type: 'callout',
        tone: 'neutral',
        title: 'When you are unsure',
        text: 'Register it. A case that turns out to belong elsewhere costs two minutes to reclassify. A case that was never registered costs a customer relationship, and it is invisible in every number we report. Over-logging is a cheap error and the reporting can absorb it.',
      },
    ],
  },

  /* ----------------------------------------------------------- 4. audience */
  {
    id: 'audience',
    n: '4',
    title: 'Audience',
    lead: 'Who must use it, and what "using it" means for each group — because it is not the same job.',
    blocks: [
      {
        type: 'table',
        columns: ['Group', 'What they do in the system', 'What they must not do'],
        rows: [
          ['Support / first responder', 'Own the queue. Register, classify, work the line-1 toolkit, close or escalate with a summary.', 'Carry a case in their head or their inbox.'],
          ['Second line / technical support', 'Take escalations, use the unit\'s production and service record, answer in writing so line 1 can reuse it.', 'Answer the same question repeatedly without writing it down for line 1.'],
          ['Production Engineering', 'Answer build, process and configuration questions through the case; receive findings that point at the process.', 'Fix something quietly without a record that it happened.'],
          ['Test Engineering', 'Interpret test and calibration records; advise on reproduction and diagnostics.', 'Give a verbal verdict that never reaches the case.'],
          ['Product Development', 'Line 3. Take genuinely new technical problems; convert findings into ECO or design change.', 'Be pulled in directly by DM, bypassing lines 1 and 2.'],
          ['Sales / Project Management', 'Register cases customers raise with them; supply commercial, warranty and site context.', 'Solve technical issues privately for their own account.'],
          ['Quality', 'Own NCR and corrective action; read the aggregated data; chair the periodic review with the process owner.', 'Wait for cases to be escalated before looking at trends.'],
          ['Service / RMA and field service', 'Receive escalated cases with full history; write findings back so the loop closes.', 'Treat the RMA as a fresh start with no support context.'],
          ['Partners / distributors', 'Line 1 for their accounts. Raise structured cases including what they already tried.', 'Forward a bare customer email with no diagnosis attached.'],
          ['Managers and leadership', 'Ask for the case number. Read the monthly picture. Protect line 3 from direct traffic.', 'Ask for a status update outside the system, which teaches everyone the system is optional.'],
        ],
      },
      {
        type: 'callout',
        tone: 'green',
        title: 'The highest-leverage behaviour in this whole plan',
        text: 'When someone brings a manager a support problem, the manager\'s first question is "what is the case number?" — every time, including when the answer is inconvenient. That one habit does more for adoption than any amount of training, because it makes the system the path of least resistance instead of extra work on top of the real work.',
      },
    ],
  },

  /* -------------------------------------------------------- 5. key messages */
  {
    id: 'messages',
    n: '5',
    title: 'Key messages',
    lead: 'Four messages, in the order people need to hear them. Say them consistently and in this order.',
    blocks: [
      {
        type: 'cards',
        items: [
          {
            meta: 'Message 1',
            title: 'This is now how we work',
            text: 'Support has one defined process and one system. From go-live, cases live there. This is not a pilot tool or an option alongside email — it is the process.',
          },
          {
            meta: 'Message 2',
            title: 'It exists to make your job easier, not to watch you',
            text: 'The point is that you stop rediscovering what a colleague already solved, stop being the only person who knows, and stop being interrupted about cases you handed over. Ownership and history protect you as much as they protect the customer.',
          },
          {
            meta: 'Message 3',
            title: 'You do not have to change how you ask for help',
            text: 'Keep asking each other in Teams. Keep picking up the phone. The only new obligation is that the outcome lands on the case the same day.',
          },
          {
            meta: 'Message 4',
            title: 'What we learn here changes the product',
            text: 'Every closed case returns a quantified result. That is what tells us which manual, which design detail, which commissioning step or which partner training is actually generating support load — and gets it fixed.',
          },
        ],
      },
      {
        type: 'callout',
        tone: 'amber',
        title: 'Say the uncomfortable part out loud',
        text: 'People will hear "more admin". Address it directly rather than hoping it does not come up: registering a case takes a couple of minutes; reconstructing a case six months later from three inboxes takes half a day, and usually fails. Acknowledge that the cost is real and front-loaded, and that it is a trade you are making deliberately.',
      },
    ],
  },

  /* ------------------------------------------------------------- 6. roles */
  {
    id: 'roles',
    n: '6',
    title: 'Roles & responsibilities',
    lead: 'Three roles must be named before this is announced. An unowned process is the single most common reason a rollout quietly stops.',
    blocks: [
      {
        type: 'table',
        columns: ['Role', 'Accountable for', 'Named'],
        rows: [
          ['Support process owner', 'The process itself: the flow document and its revisions, the tier definitions, the classification rules, the reporting, the periodic review. Decides what the process is.', '[to be assigned]'],
          ['System / tool owner', 'The platform: access, configuration, channel integrations, data quality, changes to the tool. Decides how the tool behaves.', '[to be assigned]'],
          ['Rollout contact', 'The named person people ask during rollout. One human, publicly named, who answers "how do I…" quickly. Can be the same person as the process owner if that is realistic for their load.', '[to be assigned]'],
          ['Quality manager', 'NCR and corrective action from support findings. Co-chairs the periodic review. Approves revisions to the flow.', '[to be assigned]'],
          ['Line 1 owner', 'The first-responder queue and its toolkit. Answers for how much is resolved at line 1.', '[to be assigned]'],
          ['Line 2 owner', 'Escalated technical cases and the second-line toolkit.', '[to be assigned]'],
          ['Line 3 / PD contact', 'Named route into Product Development, plus the rule for what qualifies to reach them.', '[to be assigned]'],
          ['Executive sponsor', 'Says publicly that this is how we work, and backs the process owner when it is inconvenient.', '[to be assigned]'],
        ],
      },
      {
        type: 'callout',
        tone: 'red',
        title: 'Do not announce before these are filled in',
        text: 'If someone asks "who do I go to?" in the first week and there is no answer, they will go back to email — and getting them back is much harder than getting them started. Process owner, system owner and rollout contact are the three that cannot be blank at announcement.',
      },
      {
        type: 'p',
        text: 'Separating process owner from system owner is deliberate. They get confused constantly, and the confusion is expensive: "the system won\'t let me" and "the process says I must" are different problems with different fixes, and one person holding both tends to answer every question as a tool question.',
      },
    ],
  },

  /* ---------------------------------------------------------- 7. channels */
  {
    id: 'channels',
    n: '7',
    title: 'Communication channels',
    lead: 'Where each kind of conversation belongs, so nobody has to guess.',
    blocks: [
      {
        type: 'table',
        columns: ['Channel', 'Use it for', 'Rule'],
        rows: [
          ['The support case', 'Everything that matters: findings, decisions, customer correspondence, evidence, handovers.', 'This is the record. If it is not here, it did not happen.'],
          ['Teams — support channel', 'Fast "does anyone know…", second opinions, heads-ups on something developing.', 'Reference the case number. Put the outcome on the case the same day.'],
          ['Teams — direct message', 'Reaching a specific expert quickly.', 'Allowed. But the answer goes on the case, and whoever asked is responsible for putting it there.'],
          ['Email — external', 'Talking to customers and partners.', 'Send from the case where the tool allows it. Otherwise attach the thread to the case.'],
          ['Email — internal', 'Not the place for case work.', 'If you receive case work by internal email, put it on the case and reply with the link.'],
          ['Phone / call', 'Urgent, ambiguous or emotionally difficult conversations. Often the right choice.', 'Log the call on the case during or immediately after — not at end of day.'],
          ['Periodic support review', 'Trends, recurring causes, decisions about what we change.', 'Fixed cadence, standing agenda, decisions recorded with owners and dates.'],
        ],
      },
      {
        type: 'callout',
        tone: 'neutral',
        title: 'One shared channel, not one per team',
        text: 'Create a single support channel that Support, PE, TE, PD and Sales are all in. Per-team channels rebuild exactly the silos this rollout is meant to remove, and they make the "did anyone already answer this?" question unanswerable.',
      },
    ],
  },

  /* ------------------------------------------------- 8. response expectations */
  {
    id: 'expectations',
    n: '8',
    title: 'Response expectations',
    lead: 'People cannot comply with a standard that has not been stated. Set these numbers before go-live — the values below are a starting proposal.',
    blocks: [
      {
        type: 'table',
        columns: ['Priority', 'What it means', 'Acknowledge', 'First substantive response', 'Owner'],
        rows: [
          ['P1 — Critical', 'Installation or vessel is down, or there is a possible safety issue.', 'Immediate, automatic', 'Same working day', 'Line 1 + escalation owner informed'],
          ['P2 — High', 'Major function unusable, no workaround, operation significantly affected.', 'Immediate, automatic', '1 working day', 'Line 1'],
          ['P3 — Normal', 'Fault with a workaround, or a question blocking progress.', 'Immediate, automatic', '2 working days', 'Line 1'],
          ['P4 — Low', 'Cosmetic, informational, or improvement request.', 'Immediate, automatic', '5 working days', 'Line 1'],
        ],
      },
      {
        type: 'list',
        items: [
          'Acknowledgement is automatic on every channel and includes the case number. It is not an answer and does not stop the clock.',
          'Priority is set at intake from severity and urgency — not from who is asking or how loudly.',
          'Anything with a possible safety or regulatory dimension is P1 by default, and leaves the tier ladder immediately. When unsure, treat it as safety-related.',
          'Out of hours: [define the route — on-call number or mailbox, and who monitors it]. Whatever route exists, the case is still created; an out-of-hours call is not an exemption from the record.',
        ],
      },
      {
        type: 'callout',
        tone: 'amber',
        title: 'Publish targets you can actually hit',
        text: 'Set these deliberately low for go-live and raise them once you can see real numbers. A published target that is routinely missed teaches everyone — internally and externally — that the published targets are decoration.',
      },
    ],
  },

  /* ---------------------------------------------------------- 9. timeline */
  {
    id: 'timeline',
    n: '9',
    title: 'Rollout timeline',
    lead: 'Six phases. Week 0 is go-live — the process owner sets the actual calendar dates.',
    blocks: [
      {
        type: 'timeline',
        items: [
          {
            phase: 'Prepare',
            when: 'Week −4 to −3',
            owner: 'Process owner + system owner',
            what: [
              'Name the roles in section 6. Nothing else starts until this is done.',
              'Agree the tier model and confirm the open questions on the flow document (service line mapping, intake target).',
              'Set the classification lists: request type, severity, priority, close reasons.',
              'Set the response targets in section 8.',
              'Create the shared Teams channel and the one-page quick card.',
            ],
            exit: 'Roles named, flow document at an agreed revision, targets set.',
          },
          {
            phase: 'Announce',
            when: 'Week −2',
            owner: 'Executive sponsor + process owner',
            what: [
              'All-hands or department-round announcement using the message in section 11.',
              'Short live demo — one real case from arrival to close, ten minutes, no slides.',
              'State the go-live date plainly: "from this date, we work like this".',
              'Managers hold a five-minute item in their own team meeting using the talking-points card.',
            ],
            exit: 'Everyone in scope has heard it from a person, not only read it.',
          },
          {
            phase: 'Pilot',
            when: 'Week −2 to −1',
            owner: 'Line 1 owner',
            what: [
              'Run all new cases from one channel — email to the support address is the honest test — fully through the process.',
              'Deliberately drive two or three cases all the way to line 3 and to RMA, to find where the handovers break.',
              'Fix what the pilot exposes before go-live rather than explaining it away afterwards.',
            ],
            exit: 'A case can traverse all three lines without anybody resorting to email.',
          },
          {
            phase: 'Go live',
            when: 'Week 0',
            owner: 'Process owner',
            what: [
              'All channels live. All new cases in the system.',
              'Rollout contact is visibly available and responds fast all week.',
              'Cases already in flight elsewhere are not migrated wholesale — finish them where they are, register anything still open after [2 weeks].',
              'Daily fifteen-minute stand-up for the first week to catch friction while it is still cheap to fix.',
            ],
            exit: 'Every new case created after this date exists in the system.',
          },
          {
            phase: 'Stabilise',
            when: 'Week 1 to 4',
            owner: 'Process owner + rollout contact',
            what: [
              'Amnesty period: when someone works around the system, help them do it properly. No blame in the first month.',
              'Collect friction points continuously — the meta-feedback route in section 12.',
              'Adjust the workflow where it is genuinely wrong. Hold the line where it is merely unfamiliar. Telling those apart is the process owner\'s main job this month.',
              'Weekly numbers to leadership from week 2, even when they look bad.',
            ],
            exit: 'Adoption measures in section 10 trending toward target; friction list shrinking.',
          },
          {
            phase: 'Review & normalise',
            when: 'Week 5 to 12',
            owner: 'Process owner + quality manager',
            what: [
              'Formal 30-day and 90-day reviews against the measures in section 10.',
              'Raise a revision of the flow document with what the first weeks taught you. Expect to.',
              'Periodic support review becomes the standing forum and starts feeding management review.',
              'From week 5, working outside the system is addressed as a normal expectation, not as a rollout issue.',
            ],
            exit: 'The process is routine; changes to it go through the revision route rather than through announcements.',
          },
        ],
      },
      {
        type: 'callout',
        tone: 'neutral',
        title: 'On migrating open cases',
        text: 'Do not bulk-migrate history. It is expensive, it produces poor records, and it stalls go-live. Draw a line at the go-live date, let existing threads finish where they are, and register anything still open after two weeks. The system earns its history forwards.',
      },
    ],
  },

  /* ------------------------------------------------------- 10. success measures */
  {
    id: 'measures',
    n: '10',
    title: 'How we will know it worked',
    lead: '"Run it for a period and collect feedback" is not falsifiable. These are.',
    blocks: [
      {
        type: 'metrics',
        items: [
          { metric: 'Cases originating in the system', target: '≥ 95% by week 8', why: 'Adoption. The rest arrive by email or phone and get registered by whoever received them.', kind: 'Adoption' },
          { metric: 'Cases with a named owner within one working day', target: '100%', why: 'Ownership. The measure that stops cases stalling silently.', kind: 'Adoption' },
          { metric: 'Cases closed with a documented cause and fix', target: '≥ 90%', why: 'Quality of record. Without this the history is worthless and the improvement loop has no input.', kind: 'Quality' },
          { metric: 'Share resolved at line 1', target: 'Establish a baseline in month 1, then improve', why: 'Efficiency. Rises as the known-issue and FAQ content grows.', kind: 'Efficiency' },
          { metric: 'Median time to first substantive response', target: 'Within the section 8 targets, 90% of the time', why: 'Responsiveness, from the customer\'s point of view.', kind: 'Efficiency' },
          { metric: 'Reopened / not-confirmed rate', target: 'Track it; falling', why: 'Whether our fixes actually hold. A rising number is a diagnosis problem, not a customer problem.', kind: 'Quality' },
          { metric: 'Customer satisfaction on close', target: 'Baseline in month 1', why: 'The only measure that reflects the customer rather than us.', kind: 'Outcome' },
          { metric: 'Support-driven improvements delivered', target: '≥ 1 per month reaching design, manual, commissioning or training', why: 'Whether the loop actually closes. If this stays at zero, the data is being collected and ignored.', kind: 'Outcome' },
          { metric: 'Devices reaching accepted warranty', target: '< 2 per 1000 produced', why: 'The reason all of this exists. Moves slowly — a lagging measure, not a rollout measure.', kind: 'Outcome' },
        ],
      },
      {
        type: 'callout',
        tone: 'green',
        title: 'The one to watch in week 2',
        text: '"Cases with a named owner within one working day." It is the earliest honest signal. Cases getting created but not picked up means people are complying with the letter of the process and not the substance — and that is a fixable management problem if you catch it early.',
      },
    ],
  },

  /* ------------------------------------------------------------ 11. training */
  {
    id: 'training',
    n: '11',
    title: 'Training & enablement',
    lead: 'Short, role-specific, and hands-on. Nobody needs a two-hour walkthrough of a tool they will use daily.',
    blocks: [
      {
        type: 'table',
        columns: ['Audience', 'Format', 'Length', 'Content'],
        rows: [
          ['Everyone', 'Live demo at the announcement + the one-page quick card', '10 min', 'One real case end to end. Where to register. What "the outcome goes on the case" means.'],
          ['Support / line 1', 'Hands-on session, then paired working for the first week', '90 min + shadowing', 'The nine-item line-1 toolkit as the actual curriculum. Classification. Writing a close that the next reader can use. Writing an escalation summary.'],
          ['Line 2 / technical', 'Hands-on session', '60 min', 'The twelve-item line-2 toolkit. Where the production, test and BOM records live. Writing an answer line 1 can reuse next time.'],
          ['PE / TE / PD', 'Briefing', '30 min', 'How you get pulled into a case, what is expected when you are, and why answering by DM alone costs the organisation the answer.'],
          ['Sales / PM', 'Briefing', '20 min', 'How to register a case a customer raised with you, in under two minutes. Why not doing it hurts your own account.'],
          ['Managers', 'Talking-points card + one briefing', '20 min', 'What to say to your team. Asking for the case number. Protecting line 3 from direct traffic.'],
          ['Partners / distributors', 'Written guide + one session per key partner', '45 min', 'What a good case from a partner contains. What they should try before raising it.'],
        ],
      },
      {
        type: 'callout',
        tone: 'neutral',
        title: 'Use the toolkits as the curriculum',
        text: 'The line-1 and line-2 toolkits in the flow document are already an ordered list of what a competent responder does. That is your training syllabus and your competence record in one — which also satisfies what ISO 9001 asks for on competence, without writing a separate training matrix.',
      },
    ],
  },

  /* -------------------------------------------------------- 12. feedback loop */
  {
    id: 'feedback',
    n: '12',
    title: 'Feedback & follow-up',
    lead: 'Two separate loops. Confusing them is why process improvements stall.',
    blocks: [
      {
        type: 'cards',
        items: [
          {
            meta: 'Loop 1',
            title: 'Feedback about the process',
            text: '"This step is in the way", "the classification list is missing an option", "the form asks the wrong question". Goes to the rollout contact during rollout, and to the periodic support review afterwards. Every item gets an answer — accepted with an owner, or declined with a reason. Silence is what kills this loop.',
          },
          {
            meta: 'Loop 2',
            title: 'Feedback from the cases themselves',
            text: 'The quantified result every closed case returns: cause class, tier that resolved it, time, cost, units exposed. Aggregated and read at the periodic review, which decides what gets fixed in design, manuals, commissioning or partner training.',
          },
        ],
      },
      {
        type: 'numbered',
        items: [
          'Week 1–4: friction log open, rollout contact answers within a day, visible list of what has been changed as a result.',
          '30-day review: measures from section 10, friction log, decisions with owners. Publish the outcome — including what was declined and why.',
          '90-day review: same again, plus the first revision of the flow document. Expect changes; a process that needed none was not being used.',
          'From then on: the periodic support review is the standing forum, and feeds management review as ISO 9001 requires.',
        ],
      },
      {
        type: 'callout',
        tone: 'amber',
        title: 'Publish what you changed',
        text: 'Nothing sustains adoption like visible evidence that feedback moved something. Keep a short running list — "you said, we changed" — and show it at the 30-day review. Equally, be visible about what you declined and why: an unanswered suggestion is read as "they are not listening", and that is what sends people back to email.',
      },
    ],
  },

  /* ----------------------------------------------------------- 13. risks */
  {
    id: 'risks',
    n: '13',
    title: 'Risks to the rollout',
    lead: 'The predictable ways this fails. Each one has a cheap mitigation if you plan it now.',
    blocks: [
      {
        type: 'table',
        columns: ['Risk', 'How it shows up', 'Mitigation'],
        rows: [
          ['Roles never get named', 'Announcement slips repeatedly; nobody can answer "who do I ask?"', 'Hard gate: no announcement until process owner, system owner and rollout contact are named.'],
          ['Leaders bypass the system', 'A manager asks for a verbal update; the case is never touched.', 'Brief managers separately. "What is the case number?" as the standard first question.'],
          ['Perceived as surveillance', 'Quiet resistance, minimal case notes, work continues in DMs.', 'Lead with message 2. Report on causes and volumes, never on individuals, and say so explicitly.'],
          ['Line 3 gets bypassed', 'PD receives DMs directly and their planned work is disrupted.', 'Named PD contact plus a written rule for what qualifies. PD redirects politely to the case instead of answering.'],
          ['Line 1 lacks content to resolve with', 'Everything escalates; line 2 becomes the real first line.', 'Seed the FAQ and known-issue lists before go-live from the last six months of RMA and email history.'],
          ['Classification lists too complex', 'Everything lands in "Other"; reporting is useless.', 'Start with the shortest list that is useful. Add categories only when "Other" grows.'],
          ['Email keeps winning', 'Customers and staff keep using it because it works for them.', 'Do not fight it — convert it. Email creates a case draft automatically, and the receiver registers rather than redirects.'],
          ['Improvement loop never closes', 'Data accumulates; nothing changes; people conclude it was pointless.', 'The periodic review must produce decisions with owners and dates. Track "improvements delivered" as a measure.'],
          ['Rollout enthusiasm fades at week 6', 'Compliance drifts once the attention moves on.', 'Put the measures on a standing leadership report from week 2, so it is normal reporting rather than a campaign.'],
        ],
      },
    ],
  },

  /* -------------------------------------------------------- 14. data & privacy */
  {
    id: 'privacy',
    n: '14',
    title: 'Data, access & privacy',
    lead: 'Worth settling before go-live rather than after the first awkward question.',
    blocks: [
      {
        type: 'list',
        items: [
          'Support cases contain named individuals at customer and partner organisations. Treat case notes as personal data and write them as if the customer will read them — because they may be entitled to.',
          'Define who can see what: internal staff, partner users on their own accounts, and customer users on their own units. Partners must not see each other\'s cases.',
          'Set a retention period for cases, attachments and correspondence, and record it in the QMS document set alongside the flow.',
          'Keep commercial and warranty-liability discussion in the linked commercial record rather than in customer-visible case notes.',
          'Reporting is on causes, volumes and cost — not on individual staff performance. State this in the announcement; it is the single most effective answer to the surveillance concern.',
        ],
      },
    ],
  },
];

/* ----------------------------------------------------- ready-to-send messages */

export const MESSAGES = [
  {
    id: 'm-announce',
    title: 'All-hands announcement',
    meta: 'From the executive sponsor · at the announcement, and in writing afterwards',
    body: `We are introducing one common Support process and one system to run it.

From [date], support-related cases are registered and followed through that system. That covers faults, technical questions, complaints, warranty enquiries and commissioning problems — whether they reach us from a customer, from a partner, or because someone asked you directly.

Why we are doing it. Right now a support case can live in one person's inbox, a Teams thread and someone's memory at the same time. That costs us three things: we solve the same problem more than once, we lose the thread when people are away, and we cannot see which product issues are actually generating the work. One source of truth fixes all three.

What it means for you. If a customer or partner raises something with you, register it — even if you go on to solve it yourself. If you discuss a case with a colleague in Teams or on the phone, that is fine and it should continue; just put the outcome on the case the same day. If you hand a case over, hand it to a named person through the system.

What it is not. This is not a way of monitoring individuals. We will report on causes, volumes and cost — never on people.

What we expect to get from it. Every closed case returns a result we can count. That is what will tell us which manuals, which design details, which commissioning steps and which partner training are generating support load, so we can fix the causes instead of handling the consequences. Our target is fewer than 2 of every 1000 devices produced ending in accepted warranty, and we cannot get there without this data.

[Name] owns the process. [Name] owns the system. [Name] is your contact during rollout — ask them anything, including the obvious questions.

There will be a short demo at [time/place]: one real case, start to finish, ten minutes.`,
  },
  {
    id: 'm-managers',
    title: 'Manager talking points',
    meta: 'For managers to use in their own team meeting · five minutes',
    body: `Cover these five points, in your own words:

1. From [date], support cases go in the support system. Not the inbox, not a chat thread. This applies to us too.

2. Keep asking each other for help however you do it now — Teams, phone, walking over. Nothing about that changes. The one new obligation: if the conversation changed the case, the outcome goes on the case the same day.

3. If a customer asks you directly, register it. You can still help them. You just cannot be the only record that it happened.

4. This is not about monitoring anyone. Reporting is on causes and volumes, never on individuals. If anyone in the team has that worry, bring it to me — I would rather hear it than have it go unsaid.

5. From now on, when you bring me a support problem, my first question will be "what is the case number?" That is not bureaucracy. It is how I can actually help, instead of getting half the story.

Then ask: what will make this hard for you specifically? Send me anything you hear — friction we do not hear about is friction we cannot fix, and that is what sends people back to email.`,
  },
  {
    id: 'm-teams',
    title: 'Teams channel post',
    meta: 'Pin this in the shared support channel · go-live week',
    body: `📌 Support cases now live in the support system.

This channel stays exactly as useful as it was — ask here, answer here, get second opinions here. Two small habits:

• Include the case number when you ask, so anyone can see the context.
• Whoever asked puts the answer on the case, the same day.

Quick reference:
• A customer asked you directly → register the case, then help.
• Arrived by email → register it. Do not send them to the form.
• Took a phone call → log it on the case during or right after the call.
• Need line 2 → escalate in the system with a summary of what you tried, not a forwarded message.
• Closing → write what was actually wrong and what actually fixed it. Not "resolved".

Questions of any kind → [rollout contact]. No question is too basic during the first weeks.`,
  },
  {
    id: 'm-card',
    title: 'One-page quick card',
    meta: 'Print it, pin it at desks, attach it to the announcement',
    body: `WORKING A SUPPORT CASE — THE SHORT VERSION

IT ARRIVES
Any channel, any person, any format → it becomes a case.
Arrived by email or phone? You register it. Never redirect the customer.

FIRST FIVE MINUTES
• Identify the unit — scan the tag or look up the service tag.
• Check history — has this customer, or this unit, been here before?
• Classify it — type, severity, priority. Safety involved? Flag it and escalate now.
• Confirm you have enough to work with. If not, ask once for everything missing.

YOU OWN IT
One named owner at all times, from creation to close. Going away? Hand over
explicitly, to a person, in the system.

WORKING IT
Work the toolkit in order. Discuss it anywhere you like — the outcome goes on
the case the same day, written by whoever had the conversation.

ESCALATING
Not a forward. Write: the symptom, what you tried, what happened, what you
think is going on, and the specific question you are asking.

CLOSING
The customer confirms it is fixed — not you.
Write what was actually wrong and what actually fixed it, in words the next
person can reuse. "Resolved" is not a close.
Customer says it is not fixed? Reopen and escalate. Do not re-run the same fix.

WHY IT MATTERS
Every closed case returns a result we count. That is how we find out what to
fix in the product — and how we get to fewer than 2 warranty cases per 1000
devices produced.

Questions: [rollout contact]`,
  },
];
